import { result, find, startsWith, get, values, filter, some } from 'lodash-es';
import moment from 'moment';
import { createSelector } from 'reselect';
import { getRouteVariantCode, isHashedRouteVariantId, dedupeRouteVariantsPreferPlain } from '../../../../utils/helpers';

export const getRouteVariantsState = state => result(state, 'control.routes.routeVariants');

export const getFiltersState = state => result(state, 'control.routes.filters');

export const getServiceDateState = state => result(state, 'control.serviceDate');

export const getRouteVariantsLoadingState = createSelector(getRouteVariantsState, routeVariantsState => result(routeVariantsState, 'isLoading'));

export const getActiveRouteVariant = createSelector(getRouteVariantsState, (routeVariantsState) => {
    const routeVariants = result(routeVariantsState, 'all');
    const activeRouteVariantId = result(routeVariantsState, 'active');
    const direct = routeVariants[activeRouteVariantId];
    if (direct) return direct;

    const activeCode = getRouteVariantCode(activeRouteVariantId);
    return find(routeVariants, ({ routeVariantId: vid }) => (
        startsWith(vid, activeRouteVariantId)
        || (activeRouteVariantId !== activeCode && vid === activeCode)
    ));
});

export const getAllRouteVariants = createSelector(getRouteVariantsState, routeVariantsState => result(routeVariantsState, 'all'));

export const getAllRouteVariantsTotal = createSelector(getAllRouteVariants, routeVariants => values(routeVariants).length);

const filterRouteVariants = (routeVariants, filters, serviceDateState, isForSearch) => {
    const { routeType, agencyId, routeShortName, routeVariantId } = filters;
    const serviceDate = get(serviceDateState, 'date');

    const filterPredicate = {};
    if (routeType) { filterPredicate.routeType = routeType; }
    if (agencyId) { filterPredicate.agencyId = agencyId; }

    const shouldFilterByRouteVariant = !isForSearch && !!routeVariantId;
    const routeVariantCode = shouldFilterByRouteVariant ? getRouteVariantCode(routeVariantId) : null;
    if (!isForSearch && !routeVariantId && routeShortName) {
        filterPredicate.routeShortName = routeShortName;
    }

    let filteredByFilters = filter(values(routeVariants), filterPredicate);
    if (shouldFilterByRouteVariant) {
        const hashedFilter = isHashedRouteVariantId(routeVariantId);
        const allRows = values(routeVariants);
        const exactHashedRowExists = hashedFilter && some(allRows, r => r.routeVariantId === routeVariantId);

        filteredByFilters = filter(filteredByFilters, (route) => {
            const { routeVariantId: id } = route;
            if (hashedFilter) {
                if (exactHashedRowExists) return id === routeVariantId;
                return id === routeVariantCode;
            }
            return startsWith(id, routeVariantId)
                || (routeVariantId !== routeVariantCode && id === routeVariantCode);
        });
    }
    const filteredByServiceDate = filter(
        filteredByFilters,
        route => moment(serviceDate).isSameOrAfter(route.serviceStartDate, 'day') && moment(serviceDate).isSameOrBefore(route.serviceEndDate, 'day'),
    );

    return dedupeRouteVariantsPreferPlain(filteredByServiceDate);
};

export const getFilteredRouteVariants = createSelector(
    getAllRouteVariants,
    getFiltersState,
    getServiceDateState,
    (routeVariants, filters, serviceDateState) => filterRouteVariants(routeVariants, filters, serviceDateState, false),
);

export const getFilteredRouteVariantsTotal = createSelector(getFilteredRouteVariants, variants => variants.length);

export const getRouteVariantsForSearch = createSelector(
    getAllRouteVariants,
    getFiltersState,
    getServiceDateState,
    (routeVariants, filters, serviceDateState) => filterRouteVariants(routeVariants, filters, serviceDateState, true),
);
